import React from 'react';
import { useTypewriter } from '@omq/shared/src/hooks/use-typewriter';

interface ChatBubbleProps {
  onClick: () => void;
  proactive?: boolean;
  // When true, the greeting typewriter runs. The container sets this once the
  // proactive card has finished opening, so typing starts after the open morph.
  active?: boolean;
  greetings?: string[];
  // Native hover tooltip for the collapsed bubble button. Set by the integration
  // snippet via data-omq-chatbot-bubble-title. Undefined/empty -> no title attribute.
  title?: string;
  // Whether the chat window is currently open (drives aria-expanded).
  isOpen?: boolean;
  // Localized UI strings from the backend messages bundle.
  labels?: Record<string, string>;
}

/**
 * ChatBubble — the clickable trigger layer inside the morphing shell.
 *
 * Renders the bubble/card glyphs. It is a sibling of <ChatWindow> inside
 * `.omq-chatbot-shell`. Visibility and the bubble→card morph are driven entirely
 * by the shell's state class (.omq-chatbot-shell--bubble / --card / --open) in CSS.
 *
 * Layers:
 * - `__icon`: the bubble's centred glyph (two-sparkle), shown only in the bubble state.
 * - `__greeting`: the proactive row — an inline `__sparkle`, the typewriter
 *   `__greeting-text`, and a blinking `__caret`. The greeting text cycles through
 *   the configured greetings via the useTypewriter hook.
 *
 * The pseudo-input pill has been removed: the proactive card now shows only the
 * sparkle and the animated greeting text.
 *
 * It is a <button> so it is keyboard-focusable and clickable while the chat is
 * collapsed. When the shell is `open`, CSS hides this trigger.
 */
export const ChatBubble: React.FC<ChatBubbleProps> = ({
  onClick,
  proactive = false,
  active = true,
  greetings,
  title,
  isOpen = false,
  labels = {},
}) => {
  const items = (greetings ?? []).filter((g) => g && g.trim().length > 0);
  const typed = useTypewriter(items, { enabled: active });
  const showGreeting = proactive && items.length > 0;

  return (
    <>
      <button
        className="omq-chatbot-bubble-trigger"
        onClick={onClick}
        aria-label={labels.ariaOpenChat || 'Open chat'}
        aria-expanded={isOpen}
        aria-controls="omq-chatbot-window"
        aria-haspopup="dialog"
        title={title}
        type="button">
        <span className="omq-chatbot-bubble__icon" aria-hidden="true" />
        {showGreeting && (
          <div className="omq-chatbot-bubble__greeting" aria-hidden="true">
            <span className="omq-chatbot-bubble__sparkle" aria-hidden="true" />
            <span className="omq-chatbot-bubble__greeting-text">
              {typed}
              <span className="omq-chatbot-bubble__caret" aria-hidden="true" />
            </span>
          </div>
        )}
      </button>
      {/* Complete greeting for screen readers, as a SIBLING of the trigger: the
          button role flattens its descendants to presentational (and aria-label
          overrides their text), so a live region inside the button would never
          be announced. The text is keyed off `active` (false until the proactive
          card has appeared): the region mounts empty and mutates when the card
          shows — a live region inserted together with its content is not
          announced. Cleared again while the chat is open so the greeting does
          not linger in the accessibility tree next to the dialog. The
          character-by-character typewriter output above stays aria-hidden so
          partial words never reach the accessibility tree. */}
      <span className="omq-sr-only" role="status" aria-live="polite">
        {showGreeting && active && !isOpen ? items[0] : ''}
      </span>
    </>
  );
};
